import { CardShell, SectionCard, SelectField } from '@atlas/ui-components'
import { Checkbox } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIgnoreUnmappedField, useUnmappedFields } from '@/hooks/useWorkfrontMappings'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { UnmappedFieldDTO } from '@/services/workfront-mappings.service'
import { IgnoreErrorDialog } from '../components/WorkfrontDialogs'
import { UnmappedFieldsTable, unmappedFieldName } from './components/UnmappedFieldRow'
import { MAPPINGS_PATHS } from './paths'
import { useReportLoadFailure } from './tab-support'

const WEEK_MS = 7 * 24 * 3600 * 1000

const optionsOf = (values: string[]) => [...new Set(values)].sort().map((value) => ({ value, label: value }))

/**
 * Unmapped fields: Workfront fields carrying values on stored objects that no
 * mapping reads. **Map this field** opens a new Fields row reading it; **Ignore**
 * asks why, and the field leaves the list at once.
 */
export function UnmappedFieldsTab() {
	const query = useUnmappedFields()
	const ignore = useIgnoreUnmappedField()
	const navigate = useNavigate()
	const [form, setForm] = useState('')
	const [objCode, setObjCode] = useState('')
	const [newOnly, setNewOnly] = useState(false)
	const [ignoring, setIgnoring] = useState<UnmappedFieldDTO | null>(null)
	useReportLoadFailure(query.isError, 'unmapped fields')

	const fields = query.data ?? []
	// Read once per render: "this week" is relative to when the list is looked at.
	const weekAgo = Date.now() - WEEK_MS
	const shown = fields.filter(
		(field) =>
			(!form || field.forms.includes(form)) &&
			(!objCode || field.objCodes.includes(objCode)) &&
			(!newOnly || (field.firstSeenAt !== null && Date.parse(field.firstSeenAt) >= weekAgo)),
	)
	const onIgnore = (reason: string) => {
		const field = ignoring
		if (!field) return
		setIgnoring(null)
		ignore.mutate(
			{ parameterId: field.parameterId, reason },
			{
				onSuccess: () => notification.success(`Field ignored: ${unmappedFieldName(field)}`),
				onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not ignore the field.')),
			},
		)
	}

	return (
		<div className="space-y-4">
			<SectionCard title="Filters">
				<div className="grid items-end gap-4 md:grid-cols-4">
					<SelectField
						label="Form"
						value={form}
						placeholder="any"
						options={optionsOf(fields.flatMap((f) => f.forms))}
						onChange={setForm}
					/>
					<SelectField
						label="Object type"
						value={objCode}
						placeholder="any"
						options={optionsOf(fields.flatMap((f) => f.objCodes))}
						onChange={setObjCode}
					/>
					<Checkbox isSelected={newOnly} onChange={setNewOnly}>
						New this week
					</Checkbox>
				</div>
			</SectionCard>
			<CardShell>
				<UnmappedFieldsTable
					fields={shown}
					filtered={fields.length > 0}
					isLoading={query.isLoading}
					isError={query.isError}
					onRetry={() => void query.refetch()}
					onMap={(field) => void navigate(MAPPINGS_PATHS.mapField(field.parameterId))}
					onIgnore={setIgnoring}
				/>
			</CardShell>
			<IgnoreErrorDialog
				open={ignoring !== null}
				title={ignoring ? unmappedFieldName(ignoring) : ''}
				heading="Ignore this field"
				consequence="It leaves the unmapped fields; the audit log keeps the reason."
				submitting={ignore.isPending}
				onCancel={() => setIgnoring(null)}
				onIgnore={onIgnore}
			/>
		</div>
	)
}
